import { useState, useRef, useEffect } from 'react'
import { clientsService } from '@/services/clients-service'

/* ── Load SheetJS from CDN ── */
declare global {
  interface Window { XLSX: any }
}

function useXLSX() {
  const [ready, setReady] = useState(!!window.XLSX)
  useEffect(() => {
    if (window.XLSX) { setReady(true); return }
    const s = document.createElement('script')
    s.src = 'https://cdn.sheetjs.com/xlsx-0.20.3/package/dist/xlsx.full.min.js'
    s.onload = () => setReady(true)
    document.head.appendChild(s)
  }, [])
  return ready ? window.XLSX : null
}

interface ParsedRow {
  firstName: string
  lastName: string
  company: string
  email: string
  phone: string
  address: string
  city: string
  state: string
  zip: string
}

interface ImportResult {
  imported: number
  errors: string[]
}

const COLUMNS = ['First Name', 'Last Name', 'Company', 'Email', 'Phone', 'Address', 'City', 'State', 'Zip']
const COLUMN_MAP: Record<string, keyof ParsedRow> = {
  'First Name': 'firstName',
  'Last Name': 'lastName',
  'Company': 'company',
  'Email': 'email',
  'Phone': 'phone',
  'Address': 'address',
  'City': 'city',
  'State': 'state',
  'Zip': 'zip',
}

export function CustomerImportPage() {
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<ParsedRow[]>([])
  const [importing, setImporting] = useState(false)
  const [result, setResult] = useState<ImportResult | null>(null)
  const [parseError, setParseError] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const XLSX = useXLSX()

  function handleDownloadTemplate() {
    if (!XLSX) return
    const ws = XLSX.utils.aoa_to_sheet([
      COLUMNS,
      ['John', 'Smith', 'Smith Construction', 'john@smithconstruction.com', '555-555-5555', '123 Main St', 'Los Angeles', 'CA', '90001'],
      ['', '', 'ABC Corp', 'contact@abccorp.com', '', '456 Oak Ave', 'San Diego', 'CA', '92101'],
    ])
    // Set column widths
    ws['!cols'] = COLUMNS.map((col) => ({ wch: col === 'Address' || col === 'Company' || col === 'Email' ? 28 : col === 'First Name' || col === 'Last Name' ? 18 : 12 }))
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Customers')
    XLSX.writeFile(wb, 'paver-customers-template.xlsx')
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (!f) return

    if (!XLSX) {
      setParseError('Spreadsheet library is still loading. Please try again in a moment.')
      return
    }

    setFile(f)
    setResult(null)
    setParseError(null)

    try {
      const buffer = await f.arrayBuffer()
      const wb = XLSX.read(buffer, { type: 'array' })

      // Accept the first sheet regardless of name
      const sheetName = wb.SheetNames[0]
      if (!sheetName) {
        setParseError('No sheets found in this file.')
        setPreview([])
        return
      }

      const ws = wb.Sheets[sheetName]
      const rows: Record<string, unknown>[] = XLSX.utils.sheet_to_json(ws)

      const parsed: ParsedRow[] = []
      for (const row of rows) {
        const item: ParsedRow = { firstName: '', lastName: '', company: '', email: '', phone: '', address: '', city: '', state: '', zip: '' }
        for (const [col, field] of Object.entries(COLUMN_MAP)) {
          const val = row[col]
          if (val !== undefined && val !== null && String(val).trim() !== '') {
            item[field] = String(val).trim()
          }
        }
        if (!item.firstName && !item.lastName && !item.company) continue
        parsed.push(item)
      }

      if (parsed.length === 0) {
        setParseError('No valid customers found. Make sure your spreadsheet has a "First Name", "Last Name", or "Company" column and at least one row of data.')
        setPreview([])
        return
      }

      setPreview(parsed)
    } catch {
      setParseError('Failed to read spreadsheet. Please make sure it is a valid .xlsx file.')
      setPreview([])
    }
  }

  const handleImport = async () => {
    if (preview.length === 0) return
    setImporting(true)
    setResult(null)
    try {
      const res = await clientsService.import(preview)
      setResult(res)
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Import failed'
      setResult({ imported: 0, errors: [msg] })
    } finally {
      setImporting(false)
    }
  }

  const handleReset = () => {
    setFile(null)
    setPreview([])
    setResult(null)
    setParseError(null)
    if (fileRef.current) fileRef.current.value = ''
  }

  return (
    <div>
      <div className="est-list-head">
        <h1 className="est-list-title">Import Customers</h1>
      </div>

      <p className="lch-page-desc">
        Download the template, fill in your customer list, and upload to bulk-import.
        Existing customers with the same name will be skipped — no duplicates created.
      </p>

      <div className="pi-cards">
        {/* Download template */}
        <div className="card pi-card">
          <div className="pi-card-icon"><DownloadIcon /></div>
          <h3 className="pi-card-title">Download Template</h3>
          <p className="pi-card-desc">
            Get the Excel template with the correct columns. Fill in your customers and upload below.
          </p>
          <button className="btn pi-btn-download" onClick={handleDownloadTemplate} disabled={!XLSX}>
            <DownloadIcon /> Download Template
          </button>
        </div>

        {/* Upload */}
        <div className="card pi-card">
          <div className="pi-card-icon"><UploadIcon /></div>
          <h3 className="pi-card-title">Upload Customer List</h3>
          <p className="pi-card-desc">
            Upload your completed spreadsheet. At least a "First Name", "Last Name", or "Company" is required per row.
          </p>
          <label className="btn pi-btn-upload">
            <UploadIcon /> {file ? file.name : 'Choose File'}
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className="pi-file-input"
            />
          </label>
        </div>
      </div>

      {parseError && <div className="pi-error">{parseError}</div>}

      {/* Preview */}
      {preview.length > 0 && !result && (
        <div className="card pi-preview">
          <div className="pi-preview-head">
            <h3>Preview — {preview.length} customers found</h3>
            <div className="pi-preview-actions">
              <button className="btn btn-ghost" onClick={handleReset}>Cancel</button>
              <button className="btn" onClick={handleImport} disabled={importing}>
                {importing ? 'Importing...' : `Import ${preview.length} Customers`}
              </button>
            </div>
          </div>
          <div className="pi-preview-group">
            <table className="pi-preview-table">
              <thead>
                <tr>
                  <th>First Name</th>
                  <th>Last Name</th>
                  <th>Company</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>City</th>
                </tr>
              </thead>
              <tbody>
                {preview.map((row, i) => (
                  <tr key={i}>
                    <td>{row.firstName || '—'}</td>
                    <td>{row.lastName || '—'}</td>
                    <td>{row.company || '—'}</td>
                    <td>{row.email || '—'}</td>
                    <td>{row.phone || '—'}</td>
                    <td>{row.city || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Result */}
      {result && (
        <div className={`card pi-result ${result.errors.length > 0 ? 'pi-result-warn' : 'pi-result-success'}`}>
          <div className="pi-result-icon">
            {result.errors.length > 0 ? <WarnIcon /> : <CheckIcon />}
          </div>
          <div>
            <h3 className="pi-result-title">
              {result.errors.length > 0
                ? `Imported ${result.imported} of ${preview.length} customers`
                : `Successfully imported ${result.imported} customers`}
            </h3>
            {result.errors.length > 0 && (
              <ul className="pi-result-errors">
                {result.errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            )}
            <button className="btn btn-ghost pi-result-reset" onClick={handleReset}>Import Another File</button>
          </div>
        </div>
      )}
    </div>
  )
}

function DownloadIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  )
}

function UploadIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  )
}

function WarnIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  )
}
